import { normalizeAppLanguage } from "util/locale";
import { apiRequest } from "util/services";
import { getMorningBriefPrompt, getTodaysBriefPrompt } from "util/morningBriefPrompt";

export const ADVISOR_POPUP_RUN_EVENT = "pivony-advisor-popup-run";

const RUN_QUEUE_KEY = "pivony_advisor_popup_prompt";
const CLICKED_PREFIX = "pivony_advisor_popup_clicked:";

// Each popup: displayMessage (what the capsule shows) + behindPrompt (sent to Sonic Advisor on click).
// Optional dashboardName pins the Advisor chat to that dashboard (matched by exact name).
// Optional period "yesterday" / "today" pins the date window to that single calendar day.
// Optional page is sent as context.page; the Advisor runs a dedicated brief flow for
// "morning_brief" and "todays_brief".
// Optional hours [from, until) limits the popup to that local-time window.
// Order = priority; only the first unclicked popup of the day is shown.
const ADVISOR_POPUPS = [
  {
    id: "morning_brief",
    displayMessage: {
      en: { title: "Your morning briefing", text: "Here's what's new in your dashboard" },
      tr: { title: "Sabah brifin", text: "Dashboard’unda neler yeni" },
    },
    behindPrompt: getMorningBriefPrompt,
    dashboardName: "SURVEY",
    period: "yesterday",
    page: "morning_brief",
    hours: [0, 15],
  },
  {
    id: "todays_brief",
    displayMessage: {
      en: { title: "Today's brief", text: "Here's how your dashboard is doing today" },
      tr: { title: "Günün brifi", text: "Dashboard’unda bugün neler oluyor" },
    },
    behindPrompt: getTodaysBriefPrompt,
    dashboardName: "SURVEY",
    period: "today",
    page: "todays_brief",
    hours: [15, 24],
  },
];

function isInHours(popup) {
  if (!popup.hours) return true;
  const hour = new Date().getHours();
  return hour >= popup.hours[0] && hour < popup.hours[1];
}

export function localDay(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

function clickedKey(userId, popupId) {
  return `${CLICKED_PREFIX}${userId}:${popupId}`;
}

function wasClickedToday(userId, popupId) {
  try {
    return localStorage.getItem(clickedKey(userId, popupId)) === localDay();
  } catch (_) {
    return false;
  }
}

export function getActiveAdvisorPopup(userId, language) {
  if (!userId) return null;
  const popup = ADVISOR_POPUPS.find((p) => isInHours(p) && !wasClickedToday(userId, p.id));
  if (!popup) return null;
  const lang = normalizeAppLanguage(language) === "tr" ? "tr" : "en";
  return {
    id: popup.id,
    ...popup.displayMessage[lang],
    lang,
    behindPrompt: popup.behindPrompt,
    dashboardName: popup.dashboardName || null,
    period: popup.period || null,
    page: popup.page || null,
  };
}

export function runAdvisorPopup(userId, popup) {
  try {
    localStorage.setItem(clickedKey(userId, popup.id), localDay());
  } catch (_) {}
  const day = { yesterday: localDay(-1), today: localDay() }[popup.period] || null;
  try {
    sessionStorage.setItem(
      RUN_QUEUE_KEY,
      JSON.stringify({
        prompt: popup.behindPrompt(popup.lang, day),
        dashboardName: popup.dashboardName,
        since: day,
        until: day,
        page: popup.page,
      })
    );
  } catch (_) {}
  window.dispatchEvent(new CustomEvent(ADVISOR_POPUP_RUN_EVENT));
}

export function takeQueuedAdvisorPopupPrompt() {
  try {
    const raw = sessionStorage.getItem(RUN_QUEUE_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(RUN_QUEUE_KEY);
    const queued = JSON.parse(raw);
    return queued && queued.prompt ? queued : null;
  } catch (_) {
    return null;
  }
}

export async function findDashboardByName(name) {
  if (!name) return null;
  const result = await apiRequest(
    "dashboards/all",
    "POST",
    { start_index: 0, num_dashboards: 50, sort: "newest", search: name },
    true
  );
  const wanted = name.trim().toLowerCase();
  const match = (result?.dashboards || []).find(
    (d) => String(d?.Name || "").trim().toLowerCase() === wanted
  );
  return match ? { id: match.ID, name: match.Name } : null;
}
